import type { RegistrationLocation, RegistrationNext, RegistrationStatus } from '@ipc/shared'

/** The sub-routes of `/join/:providerId` (T19 fills face, wait, status and return). */
export const STEP_PATHS = {
  about: 'about',
  email: 'email',
  place: 'place',
  document: 'document',
  face: 'face',
  wait: 'wait',
  status: 'status',
} as const

/** The path for a server `next` answer. `login` leaves the join flow. */
export function pathForNext(providerId: string, next: RegistrationNext): string {
  switch (next) {
    case 'verify_email':
      return `/join/${providerId}/${STEP_PATHS.email}`
    case 'location':
      return `/join/${providerId}/${STEP_PATHS.place}`
    case 'face':
      return `/join/${providerId}/${STEP_PATHS.face}`
    case 'wait':
      return `/join/${providerId}/${STEP_PATHS.wait}`
    case 'status':
      return `/join/${providerId}/${STEP_PATHS.status}`
    case 'login':
      return '/login'
    case 'document':
      return `/join/${providerId}/document`
  }
}

/**
 * The step a registration is at, from the server's stage and status (never from local state). The same mapping as the
 * API's `nextStep` (`apps/api/src/registration/status.ts`): a failed or expired try with tries left goes back to the
 * face step; every other decision lands on the status page, which turns an approved or review registration into the
 * voter session (`memberSessionClaim`, passwordless spec §2) and goes on to the voter page.
 */
export function nextForStatus(
  s: Pick<RegistrationStatus, 'stage' | 'status'> & {
    location?: Pick<RegistrationLocation, 'missing'>
  }
): RegistrationNext {
  switch (s.stage) {
    case 'email_pending':
      return 'verify_email'
    case 'email_verified':
      return 'location'
    case 'location_set':
      // Location v2: the face check cannot start without the device location the policy asks for (back to S3).
      if (s.location?.missing) return 'location'
      return 'face'
    case 'biometric_pending':
      return 'wait'
    case 'document_pending':
      // Face first (spec §4.4): the face decision is stored; the document step settles the registration.
      return 'document'
    case 'decided':
      if (s.status === 'retry_allowed') return 'face'
      return 'status'
  }
}

export function pathForStatus(s: RegistrationStatus): string {
  return pathForNext(s.provider, nextForStatus(s))
}

/** Order of the pages that need a registration, to tell "ahead of the registration" from "behind it". */
export const PAGE_RANK = { email: 1, place: 2, document: 4 } as const
const NEXT_RANK: Record<RegistrationNext, number> = {
  verify_email: 1,
  location: 2,
  face: 3,
  wait: 3,
  document: 4,
  status: 5,
  login: 5,
}

/**
 * Where a step page should send the member, or null when the page is right. A page is wrong when the registration
 * is behind it (the code is not verified yet) or when the registration is past it and has no way back.
 */
export function redirectFor(page: keyof typeof PAGE_RANK, s: RegistrationStatus): string | null {
  const next = nextForStatus(s)
  const rank = NEXT_RANK[next]
  const pageRank = PAGE_RANK[page]
  if (rank < pageRank) return pathForStatus(s)
  // Past the page: the e-mail step cannot be redone; the place can be changed until the face check.
  if (page === 'place' ? rank > 3 : rank > pageRank) return pathForStatus(s)
  return null
}

/**
 * Where a page sends a member whose registration cookie has ended: the welcome page, with a line that says so.
 * Spread into `<Navigate {...registrationEndedPath(id)} replace />`, or `navigate(r.to, { state: r.state })`.
 */
export const registrationEndedPath = (providerId?: string) => ({
  to: providerId ? `/join/${providerId}` : '/join',
  state: { ended: true },
})
